import { BroadcastChannel } from 'broadcast-channel';
import { useEffect, useMemo } from 'react';
import { useCurrentCaseId } from 'hooks/useCurrentCase';
import type { WithApolloContext } from 'modules/Apollo/types';
import type { HPActionShortcutHandlers } from './hooks/useHPActionShortcuts';
import { useHPActionShortcuts } from './hooks/useHPActionShortcuts';

/** The shortcut ids handled by the HP widget's menu actions (Create, Edit, Reset All, Browse). */
const HP_WIDGET_SHORTCUT_IDS = ['HP_CREATE_NEW', 'HP_EDIT', 'HP_RESET', 'HP_BROWSE'] as const;

export type HPWidgetShortcutId = (typeof HP_WIDGET_SHORTCUT_IDS)[number];

export type HPWidgetShortcutHandlers = Record<HPWidgetShortcutId, () => void | boolean>;

export type HPWidgetShortcutMessage = {
  type: 'hp-widget-shortcut';
  id: HPWidgetShortcutId;
  /**
   * The case the press was made on. The channel reaches every same-origin tab, so a widget open on another case must
   * not act on it.
   */
  caseSmid: string;
};

/**
 * Carries HP widget shortcuts pressed in a window that has no HP widget over to the one that does.
 *
 * The widget only mounts on viewer/0, and a Mousetrap binding only hears key presses in its own window, so with the
 * active viewport in viewer/1 these shortcuts used to do nothing (VX-5090). Rather than mount a second widget, viewer/1
 * binds the same keys and posts the action id here; viewer/0's widget runs its own handler, so every gate it applies (a
 * surface already open, a preview, no applied protocol) applies to a forwarded press too. The form or modal opens in
 * viewer/0, the same as clicking the widget would, and viewer/1's NoticeBox reflects it as it already does.
 *
 * Unlike `hp-invalidation`, these messages are user actions, so each carries the case it was pressed on and a widget
 * acts only on its own case's presses. Otherwise a second case open in another tab would open, edit or reset its own
 * HP.
 */
export const hpWidgetShortcutChannel = new BroadcastChannel<HPWidgetShortcutMessage>(
  'hp-widget-shortcut'
);

const NO_HANDLERS: HPActionShortcutHandlers = {};

/**
 * Binds the HP widget shortcuts in a window without the widget and forwards each press to the window that has it.
 *
 * A forwarded press always consumes the key: whether the widget accepts it is only known in the other window, after the
 * event has been handled here. Nothing is bound without a current case, since there is no case to address the press to.
 */
export function useForwardHPWidgetShortcuts({
  enabled,
  context,
}: WithApolloContext & { enabled: boolean }): void {
  const caseSmid = useCurrentCaseId();

  const handlers = useMemo<HPActionShortcutHandlers>(() => {
    if (!enabled || caseSmid == null) return NO_HANDLERS;
    return Object.fromEntries(
      HP_WIDGET_SHORTCUT_IDS.map((id) => [
        id,
        () => {
          hpWidgetShortcutChannel.postMessage({ type: 'hp-widget-shortcut', id, caseSmid });
        },
      ])
    );
  }, [enabled, caseSmid]);

  useHPActionShortcuts(handlers, { context });
}

/**
 * Runs the HP widget's shortcut handlers for presses forwarded from another window on the same case. Memoize `handlers`
 * at the call site so the listener is not re-registered on every render.
 */
export function useReceiveHPWidgetShortcuts(handlers: HPWidgetShortcutHandlers): void {
  const caseSmid = useCurrentCaseId();

  useEffect(() => {
    if (caseSmid == null) return;
    const listener = (message: HPWidgetShortcutMessage) => {
      if (message.caseSmid !== caseSmid) return;
      handlers[message.id]?.();
    };
    hpWidgetShortcutChannel.addEventListener('message', listener);
    return () => {
      hpWidgetShortcutChannel.removeEventListener('message', listener);
    };
  }, [handlers, caseSmid]);
}
